import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calendar,
  Users,
  Clock,
  TrendingUp,
  Download,
  Filter,
  Search,
  X,
  Plus,
  Edit2,
  MessageSquare,
  ChevronDown,
  Infinity,
  Trash2,
  Check,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import SearchableDropdown from "../components/SearchableDropdown";
import "../styles/Presence.css";

const Presence = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlMonth = searchParams.get("month");
  const [presenceData, setPresenceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("employee_name");

  // Add Presence Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addFormData, setAddFormData] = useState({
    customer_id: "",
    employee_id: "",
    month: "",
    total_regular_hours: "",
    total_weekend_hours: "",
  });
  const [addFormLoading, setAddFormLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState("asc");

  // Edit Presence Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPresence, setEditingPresence] = useState(null);
  const [editFormData, setEditFormData] = useState({
    total_regular_hours: "",
    total_weekend_hours: "",
    notes: "",
  });
  const [editFormLoading, setEditFormLoading] = useState(false);

  // Filter states
  const [searchName, setSearchName] = useState("");
  const [selectedEmployeeType, setSelectedEmployeeType] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedContractor, setSelectedContractor] = useState("");
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [availableContractors, setAvailableContractors] = useState([]);
  const [filterMultiSite, setFilterMultiSite] = useState(false);

  // Notes state
  const [employeeNotes, setEmployeeNotes] = useState({});
  const [hoveredNote, setHoveredNote] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [notesPopupOpen, setNotesPopupOpen] = useState(false);
  const [notesPopupData, setNotesPopupData] = useState(null);
  const [popupNewNoteText, setPopupNewNoteText] = useState("");
  const [popupNewNoteType, setPopupNewNoteType] = useState("monthly");
  const [popupEditingNoteId, setPopupEditingNoteId] = useState(null);
  const [popupEditingNoteText, setPopupEditingNoteText] = useState("");

  useEffect(() => {
    // If URL month is provided, use it directly without fetching available months
    if (urlMonth) {
      setSelectedMonth(urlMonth);
    } else {
      loadAvailableMonths();
    }
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadPresenceData();
      loadSummary();
      // Load available months in background for the dropdown (only if not already loaded)
      if (availableMonths.length === 0) {
        loadAvailableMonthsForDropdown();
      }
    }
  }, [selectedMonth]);

  const loadAvailableMonthsForDropdown = async () => {
    try {
      const response = await apiService.getPresenceAvailableMonths();
      if (response.success && response.months && response.months.length > 0) {
        setAvailableMonths(response.months);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadAvailableMonths = async () => {
    try {
      const response = await apiService.getPresenceAvailableMonths();
      if (response.success && response.months && response.months.length > 0) {
        setAvailableMonths(response.months);
        setSelectedMonth(response.months[0]);
      } else {
        const fallbackMonth = new Date().toISOString().slice(0, 7);
        setSelectedMonth(fallbackMonth);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
      const fallbackMonth = new Date().toISOString().slice(0, 7);
      setSelectedMonth(fallbackMonth);
    }
  };

  const loadPresenceData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPresence(selectedMonth);
      if (response.success) {
        const data = response.data || [];
        setPresenceData(data);
        setFilteredData(data);

        // Extract unique customers and sites for filters
        const customers = [
          ...new Set(data.map((item) => item.customer_name).filter(Boolean)),
        ];
        setAvailableCustomers(customers.sort());

        const sites = [
          ...new Set(data.map((item) => item.site_name).filter(Boolean)),
        ];
        setAvailableSites(sites.sort());

        const contractors = [
          ...new Set(data.map((item) => item.contractor).filter(Boolean)),
        ];
        setAvailableContractors(contractors.sort());

        // Load notes for all employees in the presence data
        loadEmployeeNotes(data);
      }
    } catch (error) {
      console.error("Failed to load presence data:", error);
      alert("Failed to load presence data");
    } finally {
      setLoading(false);
    }
  };

  // Load notes for all employees in presence data
  const loadEmployeeNotes = async (data) => {
    const uniqueEmployeeIds = [
      ...new Set(data.map((item) => item.employee_id)),
    ];
    const notesMap = {};

    // Fetch notes for each unique employee
    for (const employeeId of uniqueEmployeeIds) {
      try {
        const response = await apiService.getEmployeeNotes(employeeId, true);
        if (response.success && response.notes) {
          notesMap[employeeId] = response.notes;
        }
      } catch (error) {
        console.error(
          `Failed to load notes for employee ${employeeId}:`,
          error,
        );
      }
    }

    setEmployeeNotes(notesMap);
  };

  const openNotesPopup = (item) => {
    setPopupNewNoteText("");
    setPopupNewNoteType("monthly");
    setPopupEditingNoteId(null);
    setPopupEditingNoteText("");
    setNotesPopupData({
      employeeName: item.employee_name,
      employeeId: item.employee_id,
      month: item.month || selectedMonth,
    });
    setNotesPopupOpen(true);
  };

  const reloadPopupNotes = async (employeeId) => {
    try {
      const response = await apiService.getEmployeeNotes(employeeId, true);
      if (response.success) {
        setEmployeeNotes((prev) => ({
          ...prev,
          [employeeId]: response.notes || [],
        }));
      }
    } catch (error) {
      console.error("Failed to reload notes:", error);
    }
  };

  const handlePopupAddNote = async () => {
    if (!popupNewNoteText.trim() || !notesPopupData) return;
    const [year, month] = (notesPopupData.month || "").split("-");
    const noteData = {
      note_text: popupNewNoteText,
      note_type: popupNewNoteType,
    };
    if (popupNewNoteType === "monthly") {
      noteData.month = parseInt(month) || new Date().getMonth() + 1;
      noteData.year = parseInt(year) || new Date().getFullYear();
    }
    try {
      await apiService.createEmployeeNote(notesPopupData.employeeId, noteData);
      setPopupNewNoteText("");
      await reloadPopupNotes(notesPopupData.employeeId);
    } catch (error) {
      console.error("Failed to add note:", error);
      alert("שגיאה בהוספת הערה");
    }
  };

  const handlePopupDeleteNote = async (noteId) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק הערה זו?")) return;
    try {
      await apiService.deleteNote(noteId);
      await reloadPopupNotes(notesPopupData.employeeId);
    } catch (error) {
      console.error("Failed to delete note:", error);
      alert("שגיאה במחיקת הערה");
    }
  };

  const handlePopupStartEdit = (note) => {
    setPopupEditingNoteId(note.note_id);
    setPopupEditingNoteText(note.note_text);
  };

  const handlePopupSaveNote = async (noteId) => {
    if (!popupEditingNoteText.trim()) return;
    try {
      await apiService.updateNote(noteId, { note_text: popupEditingNoteText });
      setPopupEditingNoteId(null);
      setPopupEditingNoteText("");
      await reloadPopupNotes(notesPopupData.employeeId);
    } catch (error) {
      console.error("Failed to update note:", error);
      alert("שגיאה בעדכון הערה");
    }
  };

  const handlePopupCancelEdit = () => {
    setPopupEditingNoteId(null);
    setPopupEditingNoteText("");
  };

  const loadSummary = async () => {
    try {
      const response = await apiService.getPresenceSummary(selectedMonth);
      if (response.success) {
        setSummary(response);
      }
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  };

  // Load customers for add modal
  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers();
      if (response.success) {
        setCustomers(response.customers || []);
      }
    } catch (error) {
      console.error("Failed to load customers:", error);
    }
  };

  // Load employees without presence for selected month/customer
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const loadEmployeesWithoutPresence = async (month, customerId) => {
    try {
      setLoadingEmployees(true);
      const response = await apiService.getEmployeesWithoutPresence(
        month,
        customerId || null,
        user?.partnership_id,
      );
      if (response.success) {
        setEmployees(response.employees || []);
      }
    } catch (error) {
      console.error("Failed to load employees:", error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Open add modal
  const openAddModal = () => {
    const month = selectedMonth || new Date().toISOString().slice(0, 7);
    setAddFormData({
      customer_id: "",
      employee_id: "",
      month: month,
      total_regular_hours: "",
      total_weekend_hours: "",
    });
    loadCustomers();
    loadEmployeesWithoutPresence(month, null);
    setShowAddModal(true);
  };

  // Handle customer change in modal - reload employees without presence
  const handleCustomerChange = (customerId) => {
    setAddFormData((prev) => ({
      ...prev,
      customer_id: customerId,
      employee_id: "",
    }));
    loadEmployeesWithoutPresence(addFormData.month, customerId);
  };

  // Handle month change in modal - reload employees without presence
  const handleMonthChange = (month) => {
    setAddFormData((prev) => ({
      ...prev,
      month: month,
      employee_id: "",
    }));
    loadEmployeesWithoutPresence(month, addFormData.customer_id);
  };

  // Handle form submission
  const handleAddPresence = async () => {
    if (!addFormData.employee_id) {
      alert("יש לבחור עובד");
      return;
    }
    if (!addFormData.month) {
      alert("יש לבחור חודש");
      return;
    }

    try {
      setAddFormLoading(true);
      const response = await apiService.createPresence({
        employee_id: parseInt(addFormData.employee_id),
        customer_id: addFormData.customer_id
          ? parseInt(addFormData.customer_id)
          : null,
        month: addFormData.month,
        total_regular_hours: parseFloat(addFormData.total_regular_hours) || 0,
        total_weekend_hours: parseFloat(addFormData.total_weekend_hours) || 0,
        partnership_id: user?.partnership_id || 1,
      });

      if (response.success) {
        alert(
          response.action === "created"
            ? "נוכחות נוספה בהצלחה"
            : "נוכחות עודכנה בהצלחה",
        );
        setShowAddModal(false);
        // Reload data if the month matches
        if (addFormData.month === selectedMonth) {
          loadPresenceData();
          loadSummary();
        }
      } else {
        alert(response.error || "שגיאה בהוספת נוכחות");
      }
    } catch (error) {
      console.error("Failed to add presence:", error);
      alert("שגיאה בהוספת נוכחות");
    } finally {
      setAddFormLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (presence) => {
    setEditingPresence(presence);
    // Get notes for this employee from employeeNotes state
    const empNotes = employeeNotes[presence.employee_id] || [];
    const monthlyNote = empNotes.find((n) => n.note_type === "monthly");
    setEditFormData({
      total_regular_hours: presence.total_regular_hours || "",
      total_weekend_hours: presence.total_weekend_hours || "",
      notes: monthlyNote?.note_text || "",
    });
    setShowEditModal(true);
  };

  // Handle edit presence
  const handleEditPresence = async () => {
    if (!editingPresence) return;

    setEditFormLoading(true);
    try {
      const response = await apiService.updatePresence(editingPresence.id, {
        total_regular_hours: parseFloat(editFormData.total_regular_hours) || 0,
        total_weekend_hours: parseFloat(editFormData.total_weekend_hours) || 0,
      });

      // Save notes if changed
      if (editFormData.notes !== undefined) {
        const [year, month] = (editingPresence.month || "").split("-");
        await apiService.createEmployeeNote(editingPresence.employee_id, {
          note_text: editFormData.notes,
          note_type: "monthly",
          month: parseInt(month) || new Date().getMonth() + 1,
          year: parseInt(year) || new Date().getFullYear(),
        });
      }

      if (response.success) {
        alert("נוכחות עודכנה בהצלחה");
        setShowEditModal(false);
        setEditingPresence(null);
        loadPresenceData();
        loadSummary();
      } else {
        alert(response.error || "שגיאה בעדכון נוכחות");
      }
    } catch (error) {
      console.error("Failed to update presence:", error);
      alert("שגיאה בעדכון נוכחות");
    } finally {
      setEditFormLoading(false);
    }
  };

  // Apply filters whenever filter values change
  useEffect(() => {
    applyFilters();
  }, [
    searchName,
    selectedEmployeeType,
    selectedCustomer,
    selectedSite,
    selectedContractor,
    filterMultiSite,
    presenceData,
  ]);

  const applyFilters = () => {
    let filtered = [...presenceData];

    // Filter by employee name (search)
    if (searchName) {
      filtered = filtered.filter((item) =>
        item.employee_name?.toLowerCase().includes(searchName.toLowerCase()),
      );
    }

    if (selectedEmployeeType) {
      filtered = filtered.filter(
        (item) => item.employee_type === selectedEmployeeType,
      );
    }

    if (selectedCustomer) {
      filtered = filtered.filter(
        (item) => item.customer_name === selectedCustomer,
      );
    }

    if (selectedSite) {
      filtered = filtered.filter((item) => item.site_name === selectedSite);
    }

    if (selectedContractor) {
      filtered = filtered.filter(
        (item) => item.contractor === selectedContractor,
      );
    }

    if (filterMultiSite) {
      const empIdCount = {};
      presenceData.forEach((item) => {
        empIdCount[item.employee_id] = (empIdCount[item.employee_id] || 0) + 1;
      });
      const multiSiteIds = new Set(
        Object.keys(empIdCount)
          .filter((id) => empIdCount[id] > 1)
          .map(Number),
      );
      filtered = filtered.filter((item) => multiSiteIds.has(item.employee_id));
    }

    setFilteredData(filtered);
    updateFilteredSummary(filtered);
  };

  const updateFilteredSummary = (data) => {
    const uniqueNames = new Set(data.map((item) => item.employee_name));
    const totalEmployees = uniqueNames.size;
    const totalRegular = data.reduce(
      (sum, item) => sum + (item.total_regular_hours || 0),
      0,
    );
    const totalWeekend = data.reduce(
      (sum, item) => sum + (item.total_weekend_hours || 0),
      0,
    );

    setSummary({
      total_employees: totalEmployees,
      total_regular_hours: totalRegular,
      total_weekend_hours: totalWeekend,
      total_hours: totalRegular + totalWeekend,
    });
  };

  const clearFilters = () => {
    setSearchName("");
    setSelectedEmployeeType("");
    setSelectedCustomer("");
    setSelectedSite("");
    setSelectedContractor("");
    setFilterMultiSite(false);
    // Reload original summary when filters are cleared
    loadSummary();
  };

  const hasActiveFilters =
    searchName ||
    selectedEmployeeType ||
    selectedCustomer ||
    selectedSite ||
    selectedContractor ||
    filterMultiSite;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortedData = () => {
    const sorted = [...filteredData].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      // Convert to numbers for numeric fields
      if (
        [
          "total_regular_hours",
          "total_weekend_hours",
          "total_hours",
          "hour_rate",
          "weekend_hour_rate",
          "previous_month_hours",
        ].includes(sortField)
      ) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const exportToExcel = async () => {
    // Dynamically import xlsx library
    const XLSX = await import("xlsx");

    const headers = [
      "שם עובד",
      "לקוח",
      "אתר",
      "סוג עובד",
      "תעריף רגיל",
      "תעריף סופ״ש",
      "שעות רגילות",
      "שעות סופ״ש",
      "סה״כ שעות",
    ];

    const rows = getSortedData().map((item) => [
      item.employee_name || "",
      item.customer_name || "",
      item.site_name || "",
      item.employee_type || "",
      item.hour_rate?.toFixed(2) || "",
      item.weekend_hour_rate?.toFixed(2) || "",
      item.total_regular_hours?.toFixed(2) || "0.00",
      item.total_weekend_hours?.toFixed(2) || "0.00",
      item.total_hours?.toFixed(2) || "0.00",
    ]);

    // Create worksheet from array of arrays
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    ws["!cols"] = [
      { wch: 20 }, // שם עובד
      { wch: 20 }, // לקוח
      { wch: 20 }, // אתר
      { wch: 15 }, // סוג עובד
      { wch: 12 }, // תעריף רגיל
      { wch: 12 }, // תעריף סופ״ש
      { wch: 12 }, // שעות רגילות
      { wch: 12 }, // שעות סופ״ש
      { wch: 12 }, // סה״כ שעות
    ];

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, formatMonth(selectedMonth));

    // Write file
    XLSX.writeFile(wb, `presence_${selectedMonth}.xlsx`);
  };

  const sortedData = getSortedData();

  // Build color map for multi-site employees (group by name)
  const multiSiteColorMap = {};
  if (filterMultiSite) {
    const multiSiteColors = [
      "rgba(59, 130, 246, 0.10)",
      "rgba(16, 185, 129, 0.10)",
      "rgba(245, 158, 11, 0.10)",
      "rgba(139, 92, 246, 0.10)",
      "rgba(236, 72, 153, 0.10)",
      "rgba(14, 165, 233, 0.10)",
      "rgba(234, 88, 12, 0.10)",
      "rgba(99, 102, 241, 0.10)",
      "rgba(20, 184, 166, 0.10)",
      "rgba(244, 63, 94, 0.10)",
      "rgba(132, 204, 22, 0.10)",
      "rgba(168, 85, 247, 0.10)",
    ];
    const multiSiteBorders = [
      "rgba(59, 130, 246, 0.35)",
      "rgba(16, 185, 129, 0.35)",
      "rgba(245, 158, 11, 0.35)",
      "rgba(139, 92, 246, 0.35)",
      "rgba(236, 72, 153, 0.35)",
      "rgba(14, 165, 233, 0.35)",
      "rgba(234, 88, 12, 0.35)",
      "rgba(99, 102, 241, 0.35)",
      "rgba(20, 184, 166, 0.35)",
      "rgba(244, 63, 94, 0.35)",
      "rgba(132, 204, 22, 0.35)",
      "rgba(168, 85, 247, 0.35)",
    ];
    const uniqueNames = [
      ...new Set(sortedData.map((item) => item.employee_name)),
    ];
    uniqueNames.forEach((name, idx) => {
      multiSiteColorMap[name] = {
        bg: multiSiteColors[idx % multiSiteColors.length],
        border: multiSiteBorders[idx % multiSiteBorders.length],
      };
    });
  }

  return (
    <div className='presence-container'>
      <div className='presence-header'>
        <div className='header-content'>
          <h1>
            <Users className='header-icon' />
            נוכחות עובדים
          </h1>
          <p className='header-subtitle'>צפייה בנתוני נוכחות חודשיים</p>
        </div>

        <div className='header-actions'>
          <div className='month-selector-enhanced'>
            <Calendar size={20} className='month-icon' />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className='month-select-enhanced'
            >
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
            <ChevronDown size={18} className='month-chevron' />
          </div>

          <button
            onClick={exportToExcel}
            className='export-btn'
            disabled={!presenceData.length}
          >
            <Download size={18} />
            ייצא ל-Excel
          </button>

          {(user?.permission === "admin" || user?.permission === "owner") && (
            <button onClick={openAddModal} className='add-presence-btn'>
              <Plus size={18} />
              הוסף נוכחות
            </button>
          )}
        </div>
      </div>

      {/* Add Presence Modal */}
      {showAddModal && (
        <div className='modal-overlay' onClick={() => setShowAddModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>הוספת נוכחות</h2>
              <button
                className='modal-close'
                onClick={() => setShowAddModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className='modal-body'>
              <div className='form-group'>
                <label>חודש *</label>
                <input
                  type='month'
                  value={addFormData.month}
                  onChange={(e) => handleMonthChange(e.target.value)}
                />
              </div>

              <div className='form-group'>
                <label>לקוח / אתר</label>
                <select
                  value={addFormData.customer_id}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value=''>כל הלקוחות</option>
                  {customers.map((customer) => (
                    <option
                      key={customer.customer_id}
                      value={customer.customer_id}
                    >
                      {customer.customer_name}
                      {customer.site_name ? ` - ${customer.site_name}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className='form-group'>
                <label>
                  עובד *{" "}
                  {loadingEmployees && (
                    <span className='loading-text'>(טוען...)</span>
                  )}
                </label>
                <select
                  value={addFormData.employee_id}
                  onChange={(e) =>
                    setAddFormData((prev) => ({
                      ...prev,
                      employee_id: e.target.value,
                    }))
                  }
                  disabled={loadingEmployees}
                >
                  <option value=''>בחר עובד...</option>
                  {employees.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.employee_name}
                      {emp.employee_number ? ` (${emp.employee_number})` : ""}
                    </option>
                  ))}
                </select>
                {!loadingEmployees && employees.length === 0 && (
                  <small className='form-hint'>
                    אין עובדים ללא נוכחות בחודש זה
                  </small>
                )}
                {employees.length > 0 && (
                  <small className='form-hint success'>
                    {employees.length} עובדים ללא נוכחות
                  </small>
                )}
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>שעות רגילות</label>
                  <input
                    type='number'
                    step='0.01'
                    min='0'
                    value={addFormData.total_regular_hours}
                    onChange={(e) =>
                      setAddFormData((prev) => ({
                        ...prev,
                        total_regular_hours: e.target.value,
                      }))
                    }
                    placeholder='0.00'
                  />
                </div>

                <div className='form-group'>
                  <label>שעות סופ״ש</label>
                  <input
                    type='number'
                    step='0.01'
                    min='0'
                    value={addFormData.total_weekend_hours}
                    onChange={(e) =>
                      setAddFormData((prev) => ({
                        ...prev,
                        total_weekend_hours: e.target.value,
                      }))
                    }
                    placeholder='0.00'
                  />
                </div>
              </div>
            </div>

            <div className='modal-footer'>
              <button
                className='btn-cancel'
                onClick={() => setShowAddModal(false)}
              >
                ביטול
              </button>
              <button
                className='btn-save'
                onClick={handleAddPresence}
                disabled={addFormLoading}
              >
                {addFormLoading ? "שומר..." : "שמור"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Presence Modal */}
      {showEditModal && editingPresence && (
        <div className='modal-overlay' onClick={() => setShowEditModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>עריכת נוכחות</h2>
              <button
                className='modal-close'
                onClick={() => setShowEditModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className='modal-body'>
              <div className='edit-info'>
                <p>
                  <strong>עובד:</strong> {editingPresence.employee_name}
                </p>
                <p>
                  <strong>לקוח:</strong> {editingPresence.customer_name || "-"}
                </p>
                <p>
                  <strong>אתר:</strong> {editingPresence.site_name || "-"}
                </p>
                <p>
                  <strong>חודש:</strong> {formatMonth(editingPresence.month)}
                </p>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>שעות רגילות</label>
                  <input
                    type='number'
                    step='0.01'
                    min='0'
                    value={editFormData.total_regular_hours}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        total_regular_hours: e.target.value,
                      }))
                    }
                    placeholder='0.00'
                  />
                </div>
                <div className='form-group'>
                  <label>שעות סופ״ש</label>
                  <input
                    type='number'
                    step='0.01'
                    min='0'
                    value={editFormData.total_weekend_hours}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        total_weekend_hours: e.target.value,
                      }))
                    }
                    placeholder='0.00'
                  />
                </div>
              </div>

              <div className='form-group'>
                <label>הערות</label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder='הוסף הערה...'
                  rows={3}
                  className='notes-textarea'
                />
              </div>
            </div>

            <div className='modal-footer'>
              <button
                className='btn-cancel'
                onClick={() => setShowEditModal(false)}
              >
                ביטול
              </button>
              <button
                className='btn-save'
                onClick={handleEditPresence}
                disabled={editFormLoading}
              >
                {editFormLoading ? "שומר..." : "עדכן"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section - Same style as Salaries page */}
      {!loading && presenceData.length > 0 && (
        <div className='filters-section'>
          <div className='search-box'>
            <Search size={18} />
            <input
              type='text'
              placeholder='חיפוש לפי שם עובד...'
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          <div className='customer-filter'>
            <Filter size={18} />
            <select
              value={selectedEmployeeType}
              onChange={(e) => setSelectedEmployeeType(e.target.value)}
            >
              <option value=''>כל הסוגים</option>
              <option value='קבלן'>קבלן</option>
              <option value='חברה'>חברה</option>
              <option value='קבלן ו חברה'>קבלן ו חברה</option>
            </select>
          </div>
          <SearchableDropdown
            options={availableCustomers}
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            placeholder='כל הלקוחות'
            searchPlaceholder='חפש לקוח...'
            icon={Filter}
            className='filter-dropdown'
          />
          <SearchableDropdown
            options={availableSites}
            value={selectedSite}
            onChange={setSelectedSite}
            placeholder='כל האתרים'
            searchPlaceholder='חפש אתר...'
            icon={Filter}
            className='filter-dropdown'
          />
          {availableContractors.length > 0 && (
            <div className='customer-filter'>
              <Filter size={18} />
              <select
                value={selectedContractor}
                onChange={(e) => setSelectedContractor(e.target.value)}
              >
                <option value=''>כל הקבלנים</option>
                {availableContractors.map((contractor) => (
                  <option key={contractor} value={contractor}>
                    {contractor}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            className={`multi-site-filter-btn${filterMultiSite ? " active" : ""}`}
            onClick={() => setFilterMultiSite((v) => !v)}
            title='הצג רק עובדים שעבדו ב-2 אתרים החודש'
          >
            <Users size={13} />2 אתרים
          </button>
        </div>
      )}

      {summary && (
        <div className='summary-cards'>
          <div className='summary-card'>
            <div className='card-icon users-icon'>
              <Users size={24} />
            </div>
            <div className='card-content'>
              <div className='card-label'>סה״כ עובדים</div>
              <div className='card-value'>{summary.total_employees}</div>
            </div>
          </div>

          <div className='summary-card'>
            <div className='card-icon clock-icon'>
              <Clock size={24} />
            </div>
            <div className='card-content'>
              <div className='card-label'>שעות רגילות</div>
              <div className='card-value'>
                {summary.total_regular_hours?.toFixed(2) || "0.00"}
              </div>
            </div>
          </div>

          <div className='summary-card'>
            <div className='card-icon weekend-icon'>
              <TrendingUp size={24} />
            </div>
            <div className='card-content'>
              <div className='card-label'>שעות סופ״ש</div>
              <div className='card-value'>
                {summary.total_weekend_hours?.toFixed(2) || "0.00"}
              </div>
            </div>
          </div>

          <div className='summary-card highlight'>
            <div className='card-icon total-icon'>
              <Clock size={24} />
            </div>
            <div className='card-content'>
              <div className='card-label'>סה״כ שעות</div>
              <div className='card-value'>
                {summary.total_hours?.toFixed(2) || "0.00"}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className='presence-table-container'>
        {loading ? (
          <div className='loading-state'>
            <div className='spinner'></div>
            <p>טוען נתונים...</p>
          </div>
        ) : presenceData.length === 0 ? (
          <div className='empty-state'>
            <Users size={48} />
            <h3>אין נתוני נוכחות</h3>
            <p>לא נמצאו נתוני נוכחות לחודש {formatMonth(selectedMonth)}</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className='empty-state'>
            <Filter size={48} />
            <h3>לא נמצאו תוצאות</h3>
            <p>אין עובדים התואמים את הסינון שנבחר</p>
            <button onClick={clearFilters} className='clear-filters-link'>
              נקה סינון
            </button>
          </div>
        ) : (
          <table className='presence-table'>
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("employee_name")}
                  className='sortable'
                >
                  שם עובד
                  {sortField === "employee_name" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("customer_name")}
                  className='sortable'
                >
                  לקוח
                  {sortField === "customer_name" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("site_name")}
                  className='sortable'
                >
                  אתר
                  {sortField === "site_name" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("employee_type")}
                  className='sortable'
                >
                  סוג עובד
                  {sortField === "employee_type" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("hour_rate")}
                  className='sortable'
                >
                  תעריף רגיל
                  {sortField === "hour_rate" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("weekend_hour_rate")}
                  className='sortable'
                >
                  תעריף סופ״ש
                  {sortField === "weekend_hour_rate" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("total_regular_hours")}
                  className='sortable'
                >
                  שעות רגילות
                  {sortField === "total_regular_hours" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("total_weekend_hours")}
                  className='sortable'
                >
                  שעות סופ״ש
                  {sortField === "total_weekend_hours" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("total_hours")}
                  className='sortable'
                >
                  סה״כ שעות
                  {sortField === "total_hours" && (
                    <span className='sort-indicator'>
                      {sortDirection === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th>הערות</th>
                {(user?.permission === "admin" ||
                  user?.permission === "owner") && (
                  <th className='actions-header'>פעולות</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item) => (
                <tr
                  key={item.id}
                  style={
                    filterMultiSite && multiSiteColorMap[item.employee_name]
                      ? {
                          backgroundColor:
                            multiSiteColorMap[item.employee_name].bg,
                          borderRight: `3px solid ${multiSiteColorMap[item.employee_name].border}`,
                        }
                      : undefined
                  }
                >
                  <td className='employee-name'>{item.employee_name}</td>
                  <td className='customer-cell'>{item.customer_name || "-"}</td>
                  <td className='site-cell'>{item.site_name || "-"}</td>
                  <td>
                    <span className={`type-badge ${item.employee_type}`}>
                      {item.employee_type || "-"}
                    </span>
                  </td>
                  <td className='rate-cell'>
                    {item.hour_rate ? `₪${item.hour_rate.toFixed(2)}` : "-"}
                  </td>
                  <td className='rate-cell'>
                    {item.weekend_hour_rate
                      ? `₪${item.weekend_hour_rate.toFixed(2)}`
                      : "-"}
                  </td>
                  <td className='hours-cell'>
                    {item.total_regular_hours?.toFixed(2) || "0.00"}
                  </td>
                  <td className='hours-cell'>
                    {item.total_weekend_hours?.toFixed(2) || "0.00"}
                  </td>
                  <td className='total-hours-cell'>
                    {item.total_hours?.toFixed(2) || "0.00"}
                  </td>
                  <td
                    className={`notes-cell clickable-cell ${employeeNotes[item.employee_id]?.length > 0 ? "has-notes" : ""}`}
                    onClick={() => openNotesPopup(item)}
                    title='לחץ לניהול הערות'
                  >
                    {employeeNotes[item.employee_id]?.length > 0 ? (
                      <div className='notes-indicator'>
                        <MessageSquare size={16} />
                        <span className='notes-count'>
                          {employeeNotes[item.employee_id].length}
                        </span>
                      </div>
                    ) : (
                      <span className='no-notes'>-</span>
                    )}
                  </td>
                  {(user?.permission === "admin" ||
                    user?.permission === "owner") && (
                    <td className='actions-cell'>
                      <button
                        className='edit-btn'
                        onClick={() => openEditModal(item)}
                        title='ערוך נוכחות'
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Notes Popup Modal */}
      {notesPopupOpen && notesPopupData && (
        <div
          className='notes-popup-overlay'
          onClick={() => setNotesPopupOpen(false)}
        >
          <div
            className='notes-popup-content'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='notes-popup-header'>
              <h3>הערות - {notesPopupData.employeeName}</h3>
              <button
                className='notes-popup-close'
                onClick={() => setNotesPopupOpen(false)}
              >
                ×
              </button>
            </div>

            {/* Add Note Form */}
            <div className='notes-popup-add-form'>
              <div className='note-type-selector'>
                <button
                  className={`note-type-btn ${popupNewNoteType === "monthly" ? "active" : ""}`}
                  onClick={() => setPopupNewNoteType("monthly")}
                >
                  <Calendar size={14} />
                  חודשי
                </button>
                <button
                  className={`note-type-btn ${popupNewNoteType === "permanent" ? "active" : ""}`}
                  onClick={() => setPopupNewNoteType("permanent")}
                >
                  <Infinity size={14} />
                  קבוע
                </button>
              </div>
              <div className='notes-popup-input-row'>
                <input
                  type='text'
                  className='notes-popup-input'
                  placeholder={
                    popupNewNoteType === "monthly"
                      ? "הוסף הערה חודשית..."
                      : "הוסף הערה קבועה..."
                  }
                  value={popupNewNoteText}
                  onChange={(e) => setPopupNewNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handlePopupAddNote();
                    }
                  }}
                />
                <button
                  className='btn-popup-add-note'
                  onClick={handlePopupAddNote}
                  disabled={!popupNewNoteText.trim()}
                  title='הוסף הערה'
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Notes List */}
            <div className='notes-popup-list'>
              {(employeeNotes[notesPopupData.employeeId] || []).length === 0 ? (
                <div className='notes-popup-empty'>אין הערות לעובד זה</div>
              ) : (
                (employeeNotes[notesPopupData.employeeId] || []).map((note) => (
                  <div key={note.note_id} className='notes-popup-item'>
                    <div className='notes-popup-item-header'>
                      <span className={`note-type-badge ${note.note_type}`}>
                        {note.note_type === "monthly"
                          ? `חודשי ${note.month}/${note.year}`
                          : "קבוע"}
                      </span>
                      {popupEditingNoteId !== note.note_id && (
                        <div className='note-actions'>
                          <button
                            className='btn-edit-note'
                            onClick={() => handlePopupStartEdit(note)}
                            title='ערוך'
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            className='btn-delete-note'
                            onClick={() => handlePopupDeleteNote(note.note_id)}
                            title='מחק'
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                    {popupEditingNoteId === note.note_id ? (
                      <div className='note-edit-form'>
                        <input
                          type='text'
                          value={popupEditingNoteText}
                          onChange={(e) =>
                            setPopupEditingNoteText(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              handlePopupSaveNote(note.note_id);
                            if (e.key === "Escape") handlePopupCancelEdit();
                          }}
                          autoFocus
                        />
                        <div className='note-edit-actions'>
                          <button
                            className='btn-save-note'
                            onClick={() => handlePopupSaveNote(note.note_id)}
                          >
                            <Check size={13} />
                          </button>
                          <button
                            className='btn-cancel-note'
                            onClick={handlePopupCancelEdit}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className='notes-popup-item-text'>
                        {note.note_text}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Presence;
